import { requestApi } from "../api/client.js";

export interface PlanCardContent {
  applicability_question: string | null;
  category: string;
  code: string;
  default_applicable: boolean;
  display_order: number;
  id: string;
  procedure_requirement: string;
  records: {
    mpi_template_name: string | null;
    notes: string | null;
    record_requirement: string;
    record_type_code: string;
  }[];
  summary_md: string | null;
  title: string;
}

export interface PlanPack {
  authority: string;
  cards: PlanCardContent[];
  code: string;
  effective_from: string | null;
  id: string;
  jurisdiction: string;
  published_on: string | null;
  source_url: string | null;
  status: "active" | "draft" | "retired";
  title: string;
  version_label: string;
}

export interface ContentPacksResponse {
  packs: PlanPack[];
  version: number;
}

export interface PlanInstanceCard {
  applicable: boolean;
  card_code: string;
  reason: string | null;
  title: string;
}

export interface PlanInstance {
  answers: Record<string, unknown>;
  cards: PlanInstanceCard[];
  effective_from: string | null;
  effective_to: string | null;
  id: string;
  location_id: string;
  plan_pack_code: string;
  published_at: string | null;
  // Null on drafts and on a default plan nobody has looked at yet. Older
  // servers don't send it.
  reviewed_at?: string | null;
  state: "draft" | "published" | "superseded";
  version_no: number;
}

export interface PlanPublishResponse {
  generated_task_definition_ids: string[];
  plan_instance: PlanInstance;
}

export interface GeneratedTaskDefinition {
  assignee_staff_profile_id?: string | null;
  assignee_staff_profile_ids?: string[];
  id: string;
  origin: "manager" | "plan_generated";
  record_type_code: string | null;
  schedule: {
    due_window_end: string | null;
    due_window_start: string | null;
    one_off_due_at: string | null;
    rrule: string | null;
  } | null;
  status: "active" | "archived" | "paused";
  task_kind: string;
  title: string;
}

// Location.plan_status. A new location gets the default plan straight away
// ("needs_review"); "none" is only for locations made before that.
export type PlanStatus = "needs_review" | "none" | "reviewed";

// From the location's published plan when the server says whether it was
// reviewed (fresher after a change on this page); otherwise the location's
// own status. On older servers a plan the kitchen published counts as
// reviewed.
export function planStatus(
  instances: readonly PlanInstance[],
  fromLocation?: unknown,
): PlanStatus {
  const published = instances.find(
    (instance) => instance.state === "published",
  );
  if (published && published.reviewed_at !== undefined)
    return published.reviewed_at === null ? "needs_review" : "reviewed";
  if (
    fromLocation === "needs_review" ||
    fromLocation === "none" ||
    fromLocation === "reviewed"
  )
    return fromLocation;
  return published ? "reviewed" : "none";
}

export function needsReview(instance: PlanInstance): boolean {
  return instance.state === "published" && instance.reviewed_at === null;
}

// "Looks right": marks the published plan as reviewed. Repeating it is
// harmless.
export function reviewPlan(planInstanceId: string): Promise<PlanInstance> {
  return requestApi<PlanInstance>(`/plan-instances/${planInstanceId}/review`, {
    body: "{}",
    method: "POST",
  });
}

export function getContentPacks(): Promise<ContentPacksResponse> {
  return requestApi<ContentPacksResponse>("/content/packs");
}

export function listPlanInstances(locationId: string): Promise<PlanInstance[]> {
  return requestApi<PlanInstance[]>(
    `/plan-instances?location_id=${encodeURIComponent(locationId)}`,
  );
}

export function createPlanDraft(input: {
  clone_current: boolean;
  location_id: string;
  plan_pack_code: string;
}): Promise<PlanInstance> {
  return requestApi<PlanInstance>("/plan-instances", {
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": crypto.randomUUID() },
    method: "POST",
  });
}

export function updatePlanCard(
  planInstanceId: string,
  card: { applicable: boolean; card_code: string; reason?: string | null },
): Promise<PlanInstance> {
  return requestApi<PlanInstance>(`/plan-instances/${planInstanceId}`, {
    body: JSON.stringify({ cards: [card] }),
    method: "PATCH",
  });
}

export function publishPlan(
  planInstanceId: string,
): Promise<PlanPublishResponse> {
  return requestApi<PlanPublishResponse>(
    `/plan-instances/${planInstanceId}/publish`,
    {
      headers: { "Idempotency-Key": crypto.randomUUID() },
      method: "POST",
    },
  );
}

export async function listGeneratedTasks(
  locationId: string,
): Promise<GeneratedTaskDefinition[]> {
  const page = await requestApi<{
    items: GeneratedTaskDefinition[];
    next_cursor: string | null;
  }>(
    `/task-definitions?location_id=${encodeURIComponent(locationId)}&limit=200`,
  );
  return page.items.filter((task) => task.origin === "plan_generated");
}

export function decisionProgress(
  pack: PlanPack,
  instance: PlanInstance,
): {
  answered: number;
  questions: number;
} {
  const questionCodes = new Set(
    pack.cards
      .filter((card) => card.applicability_question !== null)
      .map((card) => card.code),
  );
  return {
    answered: instance.cards.filter((card) => questionCodes.has(card.card_code))
      .length,
    questions: questionCodes.size,
  };
}

export interface PlanQuestions {
  common: PlanCardContent[];
  specialist: PlanCardContent[];
}

// Specialist processes (sushi, doner, sous vide…) are rare and default to no,
// so they sit behind one disclosure.
export function planQuestions(pack: PlanPack): PlanQuestions {
  const questions = pack.cards
    .filter((card) => card.applicability_question !== null)
    .sort((a, b) => a.display_order - b.display_order);
  return {
    common: questions.filter((card) => card.category !== "specialist"),
    specialist: questions.filter((card) => card.category === "specialist"),
  };
}

// A window that opens at midnight is the whole day, so it says nothing.
export function windowStartLabel(start: string | null | undefined): string {
  if (!start || /^00:00(:00)?$/.test(start)) return "";
  return `from ${start.slice(0, 5)}`;
}

export function recurrenceLabel(rrule: string | null | undefined): string {
  if (!rrule) return "One-off schedule";
  const frequency = /FREQ=([^;]+)/.exec(rrule)?.[1]?.toLowerCase();
  const interval = /INTERVAL=(\d+)/.exec(rrule)?.[1];
  if (!frequency) return "Recurring schedule";
  if (interval && interval !== "1")
    return `Every ${interval} ${frequency.replace(/ly$/, "")}s`;
  return frequency === "daily"
    ? "Every day"
    : frequency === "weekly"
      ? "Every week"
      : frequency === "monthly"
        ? "Every month"
        : `Every ${frequency.replace(/ly$/, "")}`;
}
